import { useSyncExternalStore } from "react";

/**
 * The "Try the new look" switch (docs/new-look/PLAN.md, step 0.1).
 *
 * On by default since step 9.2; "Back to the classic look" turns it off, and
 * that choice is saved per browser. A link with `?newlook=1` turns it on (and
 * `?newlook=0` turns it off) when the app first loads. Every component reading
 * it updates together, including other open tabs.
 */
export const NEW_LOOK_STORAGE_KEY = "compbox-new-look";

const listeners = new Set<() => void>();
let current: boolean | null = null;

function readStored(): boolean {
  try {
    return localStorage.getItem(NEW_LOOK_STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
}

function store(on: boolean): void {
  try {
    localStorage.setItem(NEW_LOOK_STORAGE_KEY, on ? "on" : "off");
  } catch {
    // Storage blocked (private mode): the switch lasts until the page reloads.
  }
}

function getSnapshot(): boolean {
  if (current === null) {
    const fromLink = new URLSearchParams(window.location.search).get("newlook");
    if (fromLink === "1" || fromLink === "0") {
      current = fromLink === "1";
      store(current);
    } else {
      current = readStored();
    }
  }
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== NEW_LOOK_STORAGE_KEY) return;
    current = e.newValue !== "off";
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function setNewLook(on: boolean): void {
  current = on;
  store(on);
  listeners.forEach((l) => {
    l();
  });
}

export function useNewLook(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribe, getSnapshot, () => true);
  return [on, setNewLook];
}

/**
 * Where an idea opens: the new idea page while the new look is on, the
 * classic page otherwise. Links from other pages use this; the idea page's
 * own "Open the classic page" link stays on /epic-requests.
 */
export function useIdeaHref(): (id: string) => string {
  const [on] = useNewLook();
  return (id) => (on ? `/ideas/${id}` : `/epic-requests/${id}`);
}

/** Tests only: forget the remembered value so the next read starts fresh. */
export function resetNewLookForTests(): void {
  current = null;
}
